import { type Static, Type } from "@sinclair/typebox";
import { TypeCompiler } from "@sinclair/typebox/compiler";
import type { UseQueryOptions } from "@tanstack/react-query";
import { inject } from "./ng";
import { safeUrl } from "./url";

export type BooleanString = "true" | "false";

export type SystemPreferences = {
	useCTBetaFeatures?: BooleanString;
	accountType?: "basic" | "standard";
	restrictedFeaturesAccess?: string;
	sitePathDistanceLimit?: `${number}`;
};

export type UserPreferences = {
	units?: "us customary" | "metric";
	showRestrictionHighlighter?: BooleanString;
	placesImportTickets?: string;
};

type Preferences = Static<typeof Preferences>;

const Preferences = Type.Record(
	Type.String(),
	Type.Union([Type.String(), Type.Null()]),
);

const PrefrencesWrapper = TypeCompiler.Compile(
	Type.Object({ preferences: Preferences }),
);

/**
 * Makes an HTTP request to preferences service and unwraps the preferences object.
 */
async function http<T>(options: {
	url: string | URL;
	method?: "GET" | "POST" | "PUT";
	data?: T;
	signal?: AbortSignal;
}) {
	const response = await fetch(
		new URL(options.url, window.$config.preferencesUrl),
		{
			headers: {
				accept: "application/json",
				authorization: `Bearer ${inject("UserProvider").getUser().authToken}`,
			},
			signal: options.signal,
		},
	);
	const json = await response.json();
	return (
		response.status === 200 && PrefrencesWrapper.Check(json)
			? json.preferences
			: {}
	) as T;
}

export const systemPreferencesQuery = {
	queryKey: ["systemPreferences"],
	queryFn({ signal }) {
		return http({
			url: safeUrl`/api/preferences/system/application/${window.$config.applicationId}/account`,
			signal,
		});
	},
} satisfies UseQueryOptions<
	SystemPreferences,
	unknown,
	SystemPreferences,
	string[]
>;

export const userPreferencesQuery = {
	queryKey: ["userPreferences"],
	queryFn({ signal }) {
		return http<UserPreferences>({
			url: safeUrl`/api/preferences/application/${window.$config.applicationId}/user`,
			signal,
		});
	},
} satisfies UseQueryOptions<
	UserPreferences,
	unknown,
	UserPreferences,
	string[]
>;

export const useCTBetaFeaturesQuery = {
	...systemPreferencesQuery,
	select(p) {
		return p.useCTBetaFeatures === "true";
	},
} satisfies UseQueryOptions<SystemPreferences, unknown, boolean, string[]>;
